import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import AddressPanel from 'components/AddressPanel/AddressPanel';
import DataSetsPanel from 'components/DataSetsPanel/DataSetsPanel';
import DataSetsRecordButtons from 'components/DataSetsRecordButtons/DataSetsRecordButtons';
import DetailSection from 'components/DetailSection/DetailSection';
import Loader from 'components/Loader/Loader';
import Modal from 'components/Modal/Modal';
import Panel from 'components/Panel/Panel';
import ProfitCenterLinks from 'components/ProfitCenterLinks/ProfitCenterLinks';
import Separator from 'components/Separator/Separator';

import GeneralPanel from './components/GeneralPanel';

import type { ICreateSupplierView } from './interfaces/ICreateSupplierView';

import styles from './sass/CreateSupplierView.module.scss';

function CreateSupplierView({
  control, onCreateSupplierSubmit, isConfirmationModalOpen, onHandleCloseConfirmation,
  onDiscardChangesConfirm, enumData, naceCodesData, naceDivisions, onNaceSelectionChange, isPageLoading,
  onFindCountries, onFindStates, onFindCities, isStatesDisabled, isCitiesDisabled, onChangeCountry, onChangeState,
  dateOpened, onDateOpenedChange, profitCenterIds, watch, setValue, defaultCountries,
  vcOptionsList, handleGetVcOptions, listPosition,
}: ICreateSupplierView) {
  const { t } = useTranslation();
  const { recordId = '' } = useParams();
  const placeholder = recordId ? t('common.notProvided') : '';

  return (
    <div className={styles.root}>
      <Panel className={styles.panel}>
        <div className={styles.wrapper}>
          <Loader isPageLoading={isPageLoading} isFullLoader={false} />
          <GeneralPanel
            control={control}
            enumData={enumData}
            dateOpened={dateOpened}
            onDateOpenedChange={onDateOpenedChange}
            recordId={recordId}
            placeholder={placeholder}
            handleGetVcOptions={handleGetVcOptions}
            vcOptionsList={vcOptionsList}
            listPosition={listPosition}
            setValue={setValue}
          />
          <DetailSection
            control={control}
            naceCodesData={naceCodesData}
            naceDivisions={naceDivisions}
            onNaceSelectionChange={onNaceSelectionChange}
            placeholder={placeholder}
            recordId={recordId}
          />
          <DataSetsPanel title={t('suppliers.selections.financials')} className={styles.fullWidth}>
            <ProfitCenterLinks
              control={control}
              profitCenterIds={profitCenterIds}
              placeholder={placeholder}
              watch={(field: string) => watch(field as never)}
              setValue={(field: string, value: unknown) => setValue(field as never, value as never)}
            />
          </DataSetsPanel>
          <AddressPanel
            control={control}
            placeholder={placeholder}
            onFindCountries={onFindCountries}
            onFindStates={onFindStates}
            onFindCities={onFindCities}
            isStatesDisabled={isStatesDisabled}
            isCitiesDisabled={isCitiesDisabled}
            onChangeCountry={onChangeCountry}
            onChangeState={onChangeState}
            defaultCountries={defaultCountries}
          />
        </div>
        <Separator />
        <Modal
          open={isConfirmationModalOpen}
          title={t('createNewUser.cancelModalTitle')}
          description={t('clients.cancelModalText')}
          confirmTitle={t('common.discardChanges')}
          closeTitle={t('common.keepEditing')}
          confirmVariant="error"
          onClose={() => onHandleCloseConfirmation(false)}
          onReject={() => onHandleCloseConfirmation(false)}
          onConfirm={onDiscardChangesConfirm}
          isLoading={isPageLoading}
        />
        <DataSetsRecordButtons
          onClose={onHandleCloseConfirmation}
          onSubmit={onCreateSupplierSubmit}
          isLoading={isPageLoading}
        />
      </Panel>
    </div>
  );
}

export default CreateSupplierView;
